<template>
  <div ref="dropdownRef"
       class="dropdown-menu-container"
  >
    <!-- Слот для триггера меню -->
    <div ref="triggerRef"
         class="dropdown-trigger"
         @click="toggleMenu"
    >
      <slot name="trigger"
            :is-open="isOpen"
      ></slot>
    </div>

    <!-- Выпадающее меню через Teleport -->
    <Teleport v-if="teleport"
              to="body"
    >
      <Transition name="dropdown">
        <div v-if="isOpen"
             ref="menuRef"
             class="dropdown-menu"
             :class="{ 'dropdown-menu--top': calculatedPosition === 'top' }"
             :style="menuStyles"
             @mouseenter="handleMenuMouseEnter"
             @mouseleave="handleMenuMouseLeave"
             @mouseover="handleMenuMouseOver"
             @mouseout="handleMenuMouseOut"
        >
          <section class="btns"
                   :style="contentStyles"
          >
            <slot name="menu"
                  :close="closeMenu"
                  :is-open="isOpen"
            ></slot>
          </section>
        </div>
      </Transition>
    </Teleport>

    <Transition v-else
                name="dropdown"
    >
      <div v-if="isOpen"
           ref="menuRef"
           class="dropdown-menu"
           :class="{ 'dropdown-menu--top': calculatedPosition === 'top' }"
           :style="menuStyles"
           @mouseenter="handleMenuMouseEnter"
           @mouseleave="handleMenuMouseLeave"
           @mouseover="handleMenuMouseOver"
           @mouseout="handleMenuMouseOut"
      >
        <section class="btns"
                 :style="contentStyles"
        >
          <slot name="menu"
                :close="closeMenu"
                :is-open="isOpen"
          ></slot>
        </section>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue';

const props = defineProps({
  // Позиция меню относительно триггера (по вертикали)
  position: {
    type: String,
    default: 'bottom',
    validator: (value) => ['top', 'bottom', 'auto'].includes(value),
  },
  // Горизонтальное выравнивание относительно триггера: левее/правее
  align: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value),
  },
  // Привязать ширину меню к ширине триггера (как в QR-меню)
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  teleport: {
    type: Boolean,
    default: true,
  },
  // Автоматически закрывать при клике вне меню
  closeOnClickOutside: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['open', 'close', 'toggle']);

// Состояние меню
const isOpen = ref(false);
const dropdownRef = ref(null);
const triggerRef = ref(null);
const menuRef = ref(null);
const triggerWidth = ref(0);

// Позиционирование
const menuPosition = ref({ top: -9999, left: null, right: null, width: null });
const calculatedPosition = ref(props.position);

// Вычисляемые стили для меню
const menuStyles = computed(() => {
  if (!props.teleport) {
    const styles = {
      position: 'absolute',
      zIndex: 99999,
      minWidth: triggerWidth.value ? `${triggerWidth.value}px` : undefined,
    };

    if (calculatedPosition.value === 'top') {
      styles.bottom = 'calc(100% + 4px)';
    } else {
      styles.top = 'calc(100% + 4px)';
    }

    if (props.align === 'right') {
      styles.right = '0px';
    } else {
      styles.left = '0px';
    }

    styles.width = 'max-content';

    return styles;
  }

  const styles = {
    position: 'fixed',
    top: `${menuPosition.value.top}px`,
    zIndex: 99999,
    width: 'max-content',
    minWidth: triggerWidth.value ? `${triggerWidth.value}px` : undefined,
  };

  if (menuPosition.value.left !== null) {
    styles.left = `${menuPosition.value.left}px`;
  }

  if (menuPosition.value.right !== null) {
    styles.right = `${menuPosition.value.right}px`;
  }

  if (menuPosition.value.width !== null) {
    styles.width = `${menuPosition.value.width}px`;
  }

  return styles;
});

const contentStyles = computed(() => {
  return {
    width: 'max-content',
    minWidth: 'max-content',
  };
});

// Функция расчета позиции меню
const calculateMenuPosition = async () => {
  if (!triggerRef.value) return;

  const triggerRect = triggerRef.value.getBoundingClientRect();
  triggerWidth.value = triggerRect.width || 0;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  // Используем фиксированные размеры меню для предварительного расчета
  let menuHeight = 200;
  let menuWidth = 226;

  // Если меню уже отрендерено, получаем его реальные размеры
  if (menuRef.value) {
    const menuRect = menuRef.value.getBoundingClientRect();
    menuHeight = menuRect.height || 200;
    menuWidth = Math.max(
      menuRect.width || 226,
      menuRef.value.querySelector('.btns')?.scrollWidth || 0,
    );
  }

  let position = props.position;
  if (props.position === 'auto') {
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
      position = 'top';
    } else {
      position = 'bottom';
    }
  }

  calculatedPosition.value = position;
  if (!props.teleport) return;

  menuWidth = Math.max(menuWidth, triggerWidth.value || 0);

  let top = triggerRect.bottom + 4; // Отступ 4px снизу от триггера
  // Горизонтальное выравнивание относительно триггера.
  let left = null;
  let right = null;
  const hasRoomToLeft = triggerRect.right - menuWidth >= 8;
  const hasRoomToRight = triggerRect.left + menuWidth <= viewportWidth - 8;

  if (props.align === 'left') {
    if (hasRoomToRight || !hasRoomToLeft) {
      left = triggerRect.left;
    } else {
      right = viewportWidth - triggerRect.right;
    }
  } else if (hasRoomToLeft || !hasRoomToRight) {
    right = viewportWidth - triggerRect.right;
  } else {
    left = triggerRect.left;
  }

  if (position === 'top') {
    top = triggerRect.top - menuHeight - 4; // Отступ 4px сверху от триггера
  }

  // Корректировка по горизонтали, чтобы меню не выходило за границы экрана
  if (left !== null) {
    if (left < 0) {
      left = 8; // Минимальный отступ от левого края
    } else if (left + menuWidth > viewportWidth) {
      left = viewportWidth - menuWidth - 8; // Минимальный отступ от правого края
    }
  }

  if (right !== null) {
    const minRight = 0;
    const maxRight = Math.max(0, viewportWidth - menuWidth);
    right = Math.min(Math.max(right, minRight), maxRight);
  }

  // Корректировка по вертикали
  if (top < 0) {
    top = 8; // Минимальный отступ от верхнего края
  } else if (top + menuHeight > viewportHeight) {
    top = viewportHeight - menuHeight - 8; // Минимальный отступ от нижнего края
  }

  menuPosition.value = { top, left, right, width: menuRef.value ? menuWidth : null };
};

// Методы
const toggleMenu = async () => {
  if (!isOpen.value) {
    // Рассчитываем позицию ДО показа меню
    await calculateMenuPosition();
    isOpen.value = true;
    emit('open');
  } else {
    isOpen.value = false;
    emit('close');
  }
  emit('toggle', isOpen.value);
};

const closeMenu = () => {
  isOpen.value = false;
  emit('close');
};

// Обработчик клика вне меню
const handleClickOutside = (event) => {
  if (!props.closeOnClickOutside || !isOpen.value) return;

  const isInsideTrigger = triggerRef.value && triggerRef.value.contains(event.target);
  const isInsideMenu = menuRef.value && menuRef.value.contains(event.target);

  if (!isInsideTrigger && !isInsideMenu) {
    // Проверяем, не произошел ли клик внутри модального окна
    const isInsideModal = event.target.closest(
      '.vfm-overlay, .vfm-content, [data-v-modal], .create-client-modal, .modal-container, .q-modal__content',
    );

    // Не закрываем меню, если клик произошел внутри модального окна
    if (!isInsideModal) {
      closeMenu();
    }
  }
};

// Обработчики для пересчета позиции при изменении размеров окна или скролле
const handleWindowResize = () => {
  if (isOpen.value) {
    requestAnimationFrame(() => calculateMenuPosition());
    setTimeout(() => {
      if (isOpen.value) calculateMenuPosition();
    }, 50);
  }
};

const handleWindowScroll = () => {
  if (isOpen.value) {
    requestAnimationFrame(() => calculateMenuPosition());
  }
};

// Обработчик нажатия Escape
const handleEscape = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeMenu();
  }
};

// Обработчики событий мыши для предотвращения всплытия hover
const handleMenuMouseEnter = (event) => {
  // Останавливаем всплытие события, чтобы hover не срабатывал на нижних элементах
  event.stopPropagation();
  event.preventDefault();
};

const handleMenuMouseLeave = (event) => {
  // Останавливаем всплытие события при покидании меню
  event.stopPropagation();
  event.preventDefault();
};

const handleMenuMouseOver = (event) => {
  // Дополнительная блокировка mouseover событий
  event.stopPropagation();
  event.preventDefault();
};

const handleMenuMouseOut = (event) => {
  // Дополнительная блокировка mouseout событий
  event.stopPropagation();
  event.preventDefault();
};

// Watcher для пересчета позиции при открытии меню
watch(isOpen, async (newValue) => {
  if (newValue) {
    await nextTick();
    await calculateMenuPosition();
    // Пересчитываем позицию еще раз после полного рендера меню
    setTimeout(() => {
      if (isOpen.value) {
        calculateMenuPosition();
      }
    }, 50);
  }
});

// Lifecycle hooks
onMounted(() => {
  if (props.closeOnClickOutside) {
    document.addEventListener('click', handleClickOutside);
  }
  document.addEventListener('keydown', handleEscape);
  window.addEventListener('resize', handleWindowResize);
  window.addEventListener('scroll', handleWindowScroll, true);
});

onUnmounted(() => {
  if (props.closeOnClickOutside) {
    document.removeEventListener('click', handleClickOutside);
  }
  document.removeEventListener('keydown', handleEscape);
  window.removeEventListener('resize', handleWindowResize);
  window.removeEventListener('scroll', handleWindowScroll, true);
});

// Expose methods for parent components
defineExpose({
  open: async () => {
    await calculateMenuPosition();
    isOpen.value = true;
    emit('open');
  },
  close: closeMenu,
  toggle: toggleMenu,
  isOpen: isOpen,
  calculatePosition: calculateMenuPosition,
});
</script>

<style scoped>
.dropdown-menu-container {
  position: relative;
  display: inline-block;
}

.dropdown-trigger {
  cursor: pointer;
  user-select: none;
}

.dropdown-menu {
  /* Позиционирование теперь управляется через :style */
  min-width: 0;
  background: transparent;
  pointer-events: auto;
  /* Дополнительные свойства для блокировки hover на нижних элементах */
  isolation: isolate;
  will-change: transform;
  /* Создаем непрозрачный слой для полной блокировки */
  opacity: 1;
  transform: translateZ(0);
}

/* Класс для анимации при позиционировании сверху */
/*
.dropdown-menu--top {
   //Позиционирование управляется JavaScript 
}*/

.btns {
  background: #ffffff;
  border-radius: 8px;
  padding: 8px 0;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 100;
  border: 1px solid #e0e0e0;
  /*
 z-index: 1000;
  border: 1px solid tomato; 
 TODO: delet fix tomato */
}

/* Анимации */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease-out;
}

.dropdown-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
